import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';

const Tenders = () => {
  const { user } = useContext(AuthContext);
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCreateTender, setShowCreateTender] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [newTender, setNewTender] = useState({
    title: '',
    description: '',
    company_name: '',
    contact_person: '',
    category: '',
    budget_min: '',
    budget_max: '',
    currency: 'IDR',
    deadline: '',
    requirements: [''],
    location: '',
    tags: []
  });
  const [proposal, setProposal] = useState({
    company_name: '',
    contact_person: '',
    proposal_amount: '',
    proposal_description: ''
  });

  useEffect(() => {
    fetchTenders();
  }, [statusFilter, categoryFilter]);

  const fetchTenders = async () => {
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (searchTerm) params.search = searchTerm;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/tenders`, { params });
      setTenders(response.data);
    } catch (error) {
      console.error('Error fetching tenders:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTender = async () => {
    try {
      const tenderData = {
        ...newTender,
        deadline: new Date(newTender.deadline).toISOString(),
        budget_min: newTender.budget_min ? parseFloat(newTender.budget_min) : null,
        budget_max: newTender.budget_max ? parseFloat(newTender.budget_max) : null,
        requirements: newTender.requirements.filter(req => req.trim() !== '')
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/tenders`, tenderData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTenders([response.data, ...tenders]);
      setShowCreateTender(false);
      setNewTender({
        title: '',
        description: '',
        company_name: '',
        contact_person: '',
        category: '',
        budget_min: '',
        budget_max: '',
        currency: 'IDR',
        deadline: '',
        requirements: [''],
        location: '',
        tags: []
      });
    } catch (error) {
      console.error('Error creating tender:', error);
    }
  };

  const submitProposal = async () => {
    try {
      const proposalData = {
        ...proposal,
        proposal_amount: parseFloat(proposal.proposal_amount)
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/tenders/${selectedTender.id}/proposals`, proposalData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setShowProposalModal(false);
      setSelectedTender(null);
      setProposal({
        company_name: '',
        contact_person: '',
        proposal_amount: '',
        proposal_description: ''
      });
      
      // Update tender proposals count
      setTenders(tenders.map(tender => 
        tender.id === selectedTender.id 
          ? { ...tender, proposals_count: tender.proposals_count + 1 }
          : tender
      ));
    } catch (error) {
      console.error('Error submitting proposal:', error);
    }
  };

  const formatCurrency = (amount, currency = 'IDR') => {
    const localeMap = {
      'IDR': 'id-ID',
      'MYR': 'ms-MY',
      'SGD': 'en-SG',
      'AUD': 'en-AU',
      'USD': 'en-US',
      'RMB': 'zh-CN',
      'EUR': 'de-DE'
    };
    
    return new Intl.NumberFormat(localeMap[currency] || 'en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysLeft = (deadline) => {
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-red-100 text-red-800';
      case 'awarded': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const addRequirement = () => {
    setNewTender({
      ...newTender,
      requirements: [...newTender.requirements, '']
    });
  };

  const updateRequirement = (index, value) => {
    const updatedRequirements = [...newTender.requirements];
    updatedRequirements[index] = value;
    setNewTender({
      ...newTender,
      requirements: updatedRequirements
    });
  };

  const removeRequirement = (index) => {
    setNewTender({
      ...newTender,
      requirements: newTender.requirements.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Active Tenders</h1>
          <p className="text-gray-600">Discover business opportunities and submit your proposals</p>
        </div>
        <button
          onClick={() => setShowCreateTender(true)}
          className="mt-4 md:mt-0 bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
        >
          📋 Post Tender
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search tenders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchTenders()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="awarded">Awarded</option>
          </select>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">All Categories</option>
            <option value="Furniture">Furniture</option>
            <option value="Textile">Textile</option>
            <option value="Electronics">Electronics</option>
            <option value="Services">Services</option>
          </select>
        </div>
      </div>

      {/* Tenders List */}
      <div className="space-y-6">
        {tenders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">No tenders found</p>
            <p className="text-gray-400">Try adjusting your search criteria or post a new tender</p>
          </div>
        ) : (
          tenders.map((tender) => (
            <div key={tender.id} className="bg-white rounded-lg shadow-sm">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center font-bold">
                      {tender.company_name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{tender.title}</h3>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <span>{tender.company_name}</span>
                        <span>•</span>
                        <span>{tender.category}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(tender.status)}`}>
                      {tender.status}
                    </span>
                    <div className="text-right text-sm">
                      <div className="text-orange-600 font-semibold">
                        ⏰ {getDaysLeft(tender.deadline)} days left
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 mb-4">{tender.description}</p>

                {/* Requirements */}
                {tender.requirements && tender.requirements.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Requirements:</h4>
                    <div className="grid md:grid-cols-2 gap-2">
                      {tender.requirements.map((req, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                          <span className="text-orange-500">•</span>
                          <span>{req}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Budget and Metadata */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                  {tender.budget_min && tender.budget_max && (
                    <div className="flex items-center space-x-1">
                      <span>💰</span>
                      <span>{formatCurrency(tender.budget_min, tender.currency)} - {formatCurrency(tender.budget_max, tender.currency)}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <span>📅</span>
                    <span>Due: {formatDate(tender.deadline)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>📍</span>
                    <span>{tender.location}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>📋</span>
                    <span>{tender.proposals_count} bids</span>
                  </div>
                </div>

                {/* Tags */}
                {tender.tags && tender.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {tender.tags.map((tag, index) => (
                      <span key={index} className="bg-orange-50 text-orange-700 px-2 py-1 rounded-full text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    Posted by {tender.posted_by_user?.full_name || 'Anonymous'}
                  </div>
                  <div className="flex space-x-3">
                    <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                      View Details
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTender(tender);
                        setShowProposalModal(true);
                      }}
                      className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                      disabled={tender.status !== 'active'}
                    >
                      Submit Proposal
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Tender Modal */}
      {showCreateTender && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Post New Tender</h2>
                <button
                  onClick={() => setShowCreateTender(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tender Title *
                    </label>
                    <input
                      type="text"
                      value={newTender.title}
                      onChange={(e) => setNewTender({...newTender, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Enter tender title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={newTender.category}
                      onChange={(e) => setNewTender({...newTender, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select category</option>
                      <option value="Furniture">Furniture</option>
                      <option value="Textile">Textile</option>
                      <option value="Electronics">Electronics</option>
                      <option value="Services">Services</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newTender.description}
                    onChange={(e) => setNewTender({...newTender, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Describe your tender requirements"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={newTender.company_name}
                      onChange={(e) => setNewTender({...newTender, company_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      value={newTender.contact_person}
                      onChange={(e) => setNewTender({...newTender, contact_person: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Contact person name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency *
                    </label>
                    <select
                      value={newTender.currency}
                      onChange={(e) => setNewTender({...newTender, currency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="IDR">IDR - Indonesian Rupiah</option>
                      <option value="MYR">MYR - Malaysian Ringgit</option>
                      <option value="SGD">SGD - Singapore Dollar</option>
                      <option value="AUD">AUD - Australian Dollar</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="RMB">RMB - Chinese Yuan</option>
                      <option value="EUR">EUR - Euro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Budget
                    </label>
                    <input
                      type="number"
                      value={newTender.budget_min}
                      onChange={(e) => setNewTender({...newTender, budget_min: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Minimum budget"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Budget
                    </label>
                    <input
                      type="number"
                      value={newTender.budget_max}
                      onChange={(e) => setNewTender({...newTender, budget_max: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Maximum budget"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deadline *
                    </label>
                    <input
                      type="datetime-local"
                      value={newTender.deadline}
                      onChange={(e) => setNewTender({...newTender, deadline: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Requirements */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key Requirements
                  </label>
                  {newTender.requirements.map((req, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={req}
                        onChange={(e) => updateRequirement(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                        placeholder={`Requirement ${index + 1}`}
                      />
                      {newTender.requirements.length > 1 && (
                        <button
                          onClick={() => removeRequirement(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addRequirement}
                    className="text-orange-600 hover:text-orange-700 text-sm"
                  >
                    + Add Requirement
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={newTender.location}
                    onChange={(e) => setNewTender({...newTender, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Project location"
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowCreateTender(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createTender}
                    disabled={!newTender.title || !newTender.description || !newTender.category || !newTender.deadline}
                    className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Post Tender
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Proposal Modal */}
      {showProposalModal && selectedTender && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Submit Proposal</h2>
                <button
                  onClick={() => setShowProposalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900">{selectedTender.title}</h3>
                <p className="text-sm text-gray-600">{selectedTender.company_name}</p>
              </div>

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={proposal.company_name}
                      onChange={(e) => setProposal({...proposal, company_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      value={proposal.contact_person}
                      onChange={(e) => setProposal({...proposal, contact_person: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Contact person name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proposal Amount (IDR) *
                  </label>
                  <input
                    type="number"
                    value={proposal.proposal_amount}
                    onChange={(e) => setProposal({...proposal, proposal_amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Your bid amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proposal Description *
                  </label>
                  <textarea
                    value={proposal.proposal_description}
                    onChange={(e) => setProposal({...proposal, proposal_description: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Describe your proposal, approach, and why you're the best fit"
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowProposalModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitProposal}
                    disabled={!proposal.company_name || !proposal.contact_person || !proposal.proposal_amount || !proposal.proposal_description}
                    className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit Proposal
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenders;